// CoachReplyMessage — one coach-authored message in the thread.
//
// Bundles the eyebrow + ReplyDocument + ReplyFooter and owns the local
// "which citation was clicked" state so clicking an inline [n] marker in
// the reply body opens the ConsultedPanel pre-expanded to that row.
//
// Extracted from Coach.tsx so the state stays per-message (not global).

import { useState } from 'react';
import type { CoachCitation } from '@gc-coach/shared';
import { useI18n } from '../i18n/index.js';
import { ReplyDocument } from './ReplyDocument.js';
import { ReplyFooter } from './ReplyFooter.js';
import type {
  FeedbackReason,
  FeedbackVerdict,
} from './FeedbackInline.js';
import styles from './Coach.module.css';

export interface CoachReplyMessageProps {
  messageId: string;
  text: string;
  citations: readonly CoachCitation[];
  verdict: FeedbackVerdict;
  onVerdict: (v: FeedbackVerdict) => void;
  onSubmitReason: (reason: FeedbackReason | null, note: string) => void | Promise<void>;
}

export function CoachReplyMessage({
  messageId,
  text,
  citations,
  verdict,
  onVerdict,
  onSubmitReason,
}: CoachReplyMessageProps) {
  const { t } = useI18n();
  const [openCitationNumber, setOpenCitationNumber] = useState<number | null>(null);

  return (
    <div className={styles.rep} data-message-id={messageId}>
      <div className={styles.repEyebrow}>{t('coach.author')}</div>
      <ReplyDocument
        text={text}
        citations={citations}
        onCitationClick={(n) => setOpenCitationNumber(n)}
      />
      <ReplyFooter
        citations={citations}
        verdict={verdict}
        onVerdict={onVerdict}
        onSubmitReason={onSubmitReason}
        openCitationNumber={openCitationNumber}
        onCitationHandled={() => setOpenCitationNumber(null)}
      />
    </div>
  );
}
